import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IOnuNote,
  IOnuOption,
  OnuAction,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';

interface INotesPage {
  notes: IOnuNote[];
  page: number;
  page_count: number;
  note_count: number;
  open_ticket_count: number;
}

/**
 * Notas de la ONU en SmartOLT: listar, agregar, resolver, reabrir y eliminar.
 */
@Component({
  selector: 'app-onu-notes',
  standalone: true,
  imports: [FormsModule, ButtonModule, DropdownModule, InputTextModule, InputTextareaModule],
  templateUrl: './onu-notes.component.html',
})
export class OnuNotesComponent implements OnInit {
  @Input({ required: true }) onuId!: number;
  @Input() assignees: IOnuOption[] = [];

  public notes: IOnuNote[] = [];
  public page = 1;
  public pageCount = 1;
  public openTickets = 0;
  public loading = false;
  public saving = false;
  public text = '';
  public assignee = '';
  /** nota con el formulario de respuesta o asignación abierto */
  public replyingId: number | null = null;
  public replyText = '';
  public assigningId: number | null = null;
  public assignTo = '';

  constructor(
    private readonly onuViewService: OnuViewService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.load(1);
  }

  /**
   * load
   */
  public async load(page: number) {
    this.loading = true;
    try {
      const response = await this.onuViewService.read<INotesPage>(this.onuId, 'notes', { page });
      this.notes = response.data?.notes ?? [];
      this.page = response.data?.page ?? page;
      this.pageCount = response.data?.page_count ?? 1;
      this.openTickets = response.data?.open_ticket_count ?? 0;
    } catch (error) {
      this.notes = [];
    } finally {
      this.loading = false;
    }
  }

  /**
   * add
   */
  public async add() {
    const note = this.text.trim();
    if (!note) return;
    this.saving = true;
    try {
      await this.onuViewService.action(this.onuId, 'note_add', {
        note,
        assigned_user_id: this.assignee || '',
      });
      this.text = '';
      this.assignee = '';
      await this.load(1);
    } catch {
      // el interceptor HTTP ya mostró el error; la nota queda escrita para reintentar
    } finally {
      this.saving = false;
    }
  }

  public async run(action: OnuAction, note: IOnuNote, body: Record<string, unknown> = {}) {
    try {
      await this.onuViewService.action(this.onuId, action, { ...body, noteId: note.id });
      await this.load(this.page);
    } catch {
      // el interceptor HTTP ya mostró el error de SmartOLT
    }
  }

  public async reply(note: IOnuNote) {
    const text = this.replyText.trim();
    if (!text) return;
    await this.run('note_reply', note, { note: text });
    this.replyingId = null;
    this.replyText = '';
  }

  public async assign(note: IOnuNote) {
    if (!this.assignTo) return;
    await this.run(note.assigned_user_id ? 'note_reassign' : 'note_assign', note, {
      assigned_user_id: this.assignTo,
    });
    this.assigningId = null;
    this.assignTo = '';
  }

  public remove(note: IOnuNote) {
    this.confirmationService.confirm({
      header: 'Eliminar nota',
      message: '¿Eliminar esta nota de SmartOLT?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.run('note_delete', note),
    });
  }
}
